import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Camera, Save, MapPin, Briefcase, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const Profile = () => {
  const { user, profile, updateProfile } = useAuth();
  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    display_name: profile?.display_name || '',
    bio: profile?.bio || '',
    location: profile?.location || '',
    role: profile?.role || 'entrepreneur',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef();

  const save = async () => {
    setSaving(true);
    try {
      let avatar_url = profile?.avatar_url;
      if (avatarFile) {
        const ext = avatarFile.name.split('.').pop();
        const path = `${user.id}/avatar-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from('avatars').upload(path, avatarFile, { upsert: true });
        if (!upErr) {
          const { data } = supabase.storage.from('avatars').getPublicUrl(path);
          avatar_url = data.publicUrl;
        }
      }
      await updateProfile({ ...form, avatar_url });
      setAvatarFile(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2200);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="heading-display text-2xl font-bold mb-1">Meu perfil</h1>
      <p className="text-xs text-slate-400 mb-6">Atualize suas informações</p>

      {/* Avatar */}
      <div className="glass-card rounded-2xl p-5 mb-4 flex items-center gap-5">
        <div className="relative">
          <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center text-2xl font-bold">
            {(avatarFile || profile?.avatar_url) ? (
              <img src={avatarFile ? URL.createObjectURL(avatarFile) : profile.avatar_url} alt="" className="w-full h-full object-cover"/>
            ) : (form.full_name || 'U').charAt(0).toUpperCase()}
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#4facfe] hover:bg-[#1e90ff] text-white flex items-center justify-center">
            <Camera size={14}/>
          </button>
          <input ref={fileRef} hidden type="file" accept="image/*" onChange={(e) => setAvatarFile(e.target.files[0])} />
        </div>
        <div>
          <div className="text-sm font-bold">{form.full_name || 'Sua identidade'}</div>
          <div className="text-[11px] text-slate-400">{user?.email}</div>
        </div>
      </div>

      <div className="glass-card rounded-2xl p-5 space-y-3">
        <Field label="Nome completo" value={form.full_name} onChange={(v) => setForm({...form, full_name: v})} />
        <Field label="Nome de exibição" value={form.display_name} onChange={(v) => setForm({...form, display_name: v})} placeholder="@beatriz_cafeteria" />
        <Field label="Bio" value={form.bio} onChange={(v) => setForm({...form, bio: v})} textarea />
        <Field label="Cidade" value={form.location} onChange={(v) => setForm({...form, location: v})} />

        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 block">Tipo de perfil</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'entrepreneur', icon: Rocket, label: 'Empreendedor' },
              { id: 'investor', icon: Briefcase, label: 'Investidor' },
            ].map(opt => (
              <button type="button" key={opt.id} onClick={() => setForm({...form, role: opt.id})}
                className={`rounded-xl py-2.5 px-3 text-xs font-semibold flex items-center justify-center gap-1.5 ${
                  form.role === opt.id
                    ? (opt.id==='entrepreneur'?'btn-gold':'btn-primary')
                    : 'glass-card text-slate-400'
                }`}>
                <opt.icon size={13}/> {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} className="w-full btn-primary rounded-xl py-3 text-sm font-bold mt-4 flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? 'Salvando...' : <><Save size={14}/> Salvar alterações</>}
      </button>
      {success && <div className="mt-3 text-center text-[11px] text-green-400">Salvo com sucesso ✓</div>}
    </div>
  );
};

const Field = ({ label, value, onChange, textarea, placeholder, type='text' }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 block">{label}</label>
    {textarea ? (
      <textarea value={value||''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50 resize-none" />
    ) : (
      <input type={type} value={value||''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50" />
    )}
  </div>
);

export default Profile;
